import React, { useState, useEffect } from "react";
import patrimonioService from "../../services/patrimonioService";
import { maskCurrency, maskCpfCnpj } from "../../utils/masks";
import { TIPOS_BAIXA } from "./patrimonioConstants";

// Converte valor mascarado ("R$ 1.234,56") em número ou string vazia
const parseCurrency = (value) => {
    if (!value) return "";
    const parsed = parseFloat(String(value).replace(/[^\d,]/g, "").replace(",", "."));
    return isNaN(parsed) ? "" : parsed;
};

/**
 * Modal de baixa de patrimônio (doação, venda, descarte, extravio ou outro).
 *
 * Props:
 * - isVisible: boolean
 * - patrimonio: bem a baixar
 * - onClose: () => void
 * - onSuccess: (patrimonioAtualizado) => void
 */
const DarBaixaModal = ({ isVisible, patrimonio, onClose, onSuccess }) => {
    const [tipo, setTipo] = useState("DOACAO");
    const [dataBaixa, setDataBaixa] = useState("");
    const [motivoBaixa, setMotivoBaixa] = useState("");
    const [destinatarioNome, setDestinatarioNome] = useState("");
    const [destinatarioTipo, setDestinatarioTipo] = useState("PF");
    const [destinatarioDocumento, setDestinatarioDocumento] = useState("");
    const [valorVenda, setValorVenda] = useState("");
    const [erro, setErro] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isVisible) {
            setTipo("DOACAO");
            setDataBaixa(new Date().toISOString().split("T")[0]);
            setMotivoBaixa("");
            setDestinatarioNome("");
            setDestinatarioTipo("PF");
            setDestinatarioDocumento("");
            setValorVenda("");
            setErro("");
        }
    }, [isVisible]);

    if (!isVisible || !patrimonio) return null;

    const tipoInfo = TIPOS_BAIXA.find((t) => t.value === tipo);
    const temDestinatario = tipoInfo?.temDestinatario;
    const labelDestinatario = tipo === "DOACAO" ? "Donatário" : "Comprador";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro("");

        if (!motivoBaixa.trim()) {
            setErro("Informe o motivo da baixa.");
            return;
        }
        if (tipo === "DOACAO" && !destinatarioNome.trim()) {
            setErro("Informe o donatário (pessoa física ou jurídica).");
            return;
        }

        const dados = {
            tipo,
            dataBaixa,
            motivoBaixa,
        };
        if (temDestinatario) {
            dados.destinatarioNome = destinatarioNome;
            dados.destinatarioTipo = destinatarioTipo;
            dados.destinatarioDocumento = destinatarioDocumento;
        }
        if (tipo === "VENDA") {
            dados.valorVenda = parseCurrency(valorVenda);
        }

        setIsSaving(true);
        try {
            const atualizado = await patrimonioService.darBaixa(patrimonio.id, dados);
            onSuccess(atualizado);
        } catch (error) {
            console.error("Erro ao dar baixa:", error);
            setErro(error.response?.data?.error || "Erro ao dar baixa no patrimônio.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-white rounded-md shadow-md border border-gray-200 p-6 flex flex-col gap-4">
                <div className="text-stone-800 text-lg font-semibold">Dar Baixa no Patrimônio</div>

                <div className="bg-stone-50 border border-slate-200 rounded-md p-3 text-sm">
                    <span className="text-slate-500">Bem: </span>
                    <span className="text-slate-700 font-medium">{patrimonio.codigo} - {patrimonio.nome}</span>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {/* Tipo de baixa */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Tipo de baixa *</label>
                            <select
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                            >
                                {TIPOS_BAIXA.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Data da baixa</label>
                            <input
                                type="date"
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                value={dataBaixa}
                                onChange={(e) => setDataBaixa(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Destinatário (doação/venda) */}
                    {temDestinatario && (
                        <div className="border border-slate-200 rounded-md p-3 flex flex-col gap-3">
                            <div className="text-slate-600 text-sm font-medium">{labelDestinatario}</div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Nome / Razão Social {tipo === "DOACAO" ? "*" : ""}
                                </label>
                                <input
                                    type="text"
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    placeholder={destinatarioTipo === "PJ" ? "Razão social da empresa" : "Nome completo"}
                                    value={destinatarioNome}
                                    onChange={(e) => setDestinatarioNome(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">Tipo de pessoa</label>
                                    <div className="flex gap-4 text-sm mt-1">
                                        <label className="inline-flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="destinatarioTipo"
                                                checked={destinatarioTipo === "PF"}
                                                onChange={() => setDestinatarioTipo("PF")}
                                            />
                                            <span>Física</span>
                                        </label>
                                        <label className="inline-flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="destinatarioTipo"
                                                checked={destinatarioTipo === "PJ"}
                                                onChange={() => setDestinatarioTipo("PJ")}
                                            />
                                            <span>Jurídica</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        {destinatarioTipo === "PJ" ? "CNPJ" : "CPF"}
                                    </label>
                                    <input
                                        type="text"
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        placeholder={destinatarioTipo === "PJ" ? "00.000.000/0000-00" : "000.000.000-00"}
                                        value={destinatarioDocumento}
                                        onChange={(e) => setDestinatarioDocumento(maskCpfCnpj(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Valor de venda */}
                    {tipo === "VENDA" && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Valor da venda (R$)</label>
                            <input
                                type="text"
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="R$ 0,00"
                                value={valorVenda}
                                onChange={(e) => setValorVenda(maskCurrency(e.target.value))}
                            />
                        </div>
                    )}

                    {/* Motivo */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Motivo da baixa *</label>
                        <textarea
                            rows={3}
                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            placeholder="Ex: doado por obsolescência, vendido em leilão, sem conserto viável..."
                            value={motivoBaixa}
                            onChange={(e) => setMotivoBaixa(e.target.value)}
                        />
                    </div>

                    <div className="text-slate-400 text-xs">
                        O bem não será apagado: ele permanece no sistema com todo o histórico, marcado com a nova
                        situação, e não poderá mais ser movimentado (a baixa pode ser revertida se necessário).
                    </div>

                    {erro && <div className="text-red-600 text-sm">{erro}</div>}

                    <div className="flex w-full justify-between gap-4 mt-2">
                        <button
                            type="button"
                            className="w-full px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="w-full px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                            disabled={isSaving}
                        >
                            {isSaving ? "Baixando..." : "Confirmar Baixa"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DarBaixaModal;
